// v2.19.22f-2 — Bouton étoile favoris réutilisable sur les cards chat.
// - Anonyme → toast d'invitation + ouvre AuthModal via prop.
// - Authentifié → POST/DELETE /api/favorites.
// - Persistant via map de favoris en cache mémoire (chargée 1× au mount global).
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { apiFetch } from "@/utils/apiClient";
import { useAuth } from "@/hooks/useAuth";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Cache mémoire global pour éviter de fetch /api/favorites à chaque mount de card.
let _favCache = null; // { keys: Set<"type:id">, ids: Map<"type:id", favId> }
let _favCachePromise = null;

async function _loadFavCache() {
  if (_favCache) return _favCache;
  if (_favCachePromise) return _favCachePromise;
  _favCachePromise = (async () => {
    try {
      const r = await apiFetch(`${API}/api/favorites`);
      if (!r.ok) {
        _favCache = { keys: new Set(), ids: new Map() };
        return _favCache;
      }
      const data = await r.json();
      const keys = new Set();
      const ids = new Map();
      for (const f of data.favorites || []) {
        const k = `${f.item_type}:${f.item_id}`;
        keys.add(k);
        ids.set(k, f.id);
      }
      _favCache = { keys, ids };
      return _favCache;
    } finally {
      _favCachePromise = null;
    }
  })();
  return _favCachePromise;
}

export function invalidateFavCache() { _favCache = null; _favCachePromise = null; }

// Listen to auth changes → vide le cache.
if (typeof window !== "undefined") {
  window.addEventListener("civ:auth-changed", invalidateFavCache);
}

export default function FavoriteStarButton({ itemType, itemId, itemLabel, itemMeta, onLoginRequest }) {
  const { isAuthenticated } = useAuth();
  const [isFav, setIsFav] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setIsFav(false);
      return;
    }
    const cache = await _loadFavCache();
    setIsFav(cache.keys.has(`${itemType}:${itemId}`));
  }, [isAuthenticated, itemType, itemId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast("⭐ Connecte-toi pour ajouter aux favoris", { duration: 3000 });
      onLoginRequest && onLoginRequest();
      return;
    }
    setBusy(true);
    try {
      const cache = await _loadFavCache();
      const key = `${itemType}:${itemId}`;
      if (cache.keys.has(key)) {
        const favId = cache.ids.get(key);
        const r = await apiFetch(`${API}/api/favorites/${encodeURIComponent(favId)}`, { method: "DELETE" });
        if (r.status === 204) {
          cache.keys.delete(key);
          cache.ids.delete(key);
          setIsFav(false);
          toast.success("Retiré des favoris");
        } else {
          toast.error(`Erreur ${r.status}`);
        }
      } else {
        const r = await apiFetch(`${API}/api/favorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ item_type: itemType, item_id: itemId, item_label: itemLabel, item_meta: itemMeta || null }),
        });
        const data = await r.json().catch(() => ({}));
        if (r.ok) {
          cache.keys.add(key);
          cache.ids.set(key, data.favorite?.id);
          setIsFav(true);
          toast.success("Ajouté aux favoris ⭐");
        } else {
          toast.error(data.error || `Erreur ${r.status}`);
        }
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`civ-fav-star ${isFav ? "civ-fav-star-on" : ""}`}
      onClick={handleClick}
      disabled={busy}
      title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
      aria-label={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
      data-testid={`fav-star-${itemType}-${itemId}`}
    >
      {isFav ? "⭐" : "☆"}
    </button>
  );
}
